import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';

/**
 * Uitklapknop (chevron) rechts op een kanaal- of poortblokje: toont/verbergt
 * de I Tjing-kaarten eronder (`hd-cycle-cards`). Eén knop voor alle blokjes,
 * zodat tooltip en gedrag overal gelijk zijn.
 */
@Component({
  selector: 'hd-cycle-cards-toggle',
  template: `
    <button
      type="button"
      class="hd-cycle-cards-toggle"
      [class.hd-cycle-cards-toggle--open]="expanded()"
      [attr.aria-expanded]="expanded()"
      [attr.aria-label]="tooltip()"
      [title]="tooltip()"
      data-test-id="cycle-cards-toggle"
      (click)="onClick($event)"
    >
      <i class="fas fa-chevron-down" aria-hidden="true"></i>
    </button>
  `,
  styles: `
    :host {
      display: inline-flex;
      align-self: center;
    }

    .hd-cycle-cards-toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      padding: 0;
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: var(--radius-sm);
      background: var(--white);
      color: var(--primary-75, #475260);
      font-size: 11px;
      cursor: pointer;
      transition: transform 0.15s ease, background-color 0.12s ease;
    }

    .hd-cycle-cards-toggle:hover {
      background: var(--highlight);
    }

    .hd-cycle-cards-toggle--open {
      transform: rotate(180deg);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdCycleCardsToggleComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly expanded = input(false);
  readonly toggle = output<void>();

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly tooltip = computed(() =>
    this.transloco.translate<string>(
      this.expanded() ? 'humanDesign.chart.cycleCards.collapseTooltip' : 'humanDesign.chart.cycleCards.expandTooltip',
      undefined,
      this.lang(),
    ));

  protected onClick(event: MouseEvent): void {
    // Binnen een blokje: niet ook het kanaal/de poort openen.
    event.stopPropagation();
    this.toggle.emit();
  }
}
